/**
 * Admin Agent Chat Streaming API
 *
 * RTK Query API with fakeBaseQuery for SSE streaming using @microsoft/fetch-event-source.
 * Handles streaming communication with the admin agent chat backend (AG-UI Protocol).
 */

import type { AGUISSEEvent, SSEPerformanceMetrics } from '@features/ai-chat-core';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { getAuth0AccessTokenSilently } from '@store/auth0BaseQuery';
import { Mutex } from 'async-mutex';

import {
  addMarkdownPayload,
  addToolCall,
  appendStreamingContent,
  appendToolCallArgs,
  clearToolCalls,
  completeWorkflow,
  finalizeMessage,
  removeToolCall,
  setActivitySnapshot,
  setAssistantStatus,
  setCurrentStep,
  setError,
  setSessionId,
  setSSEPerformanceMetrics,
  setThreadId,
  startStreamingMessage,
} from './agentChatSlice';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

// ============================================================================
// Types
// ============================================================================

export interface AdminAgentChatRequest {
  prompt: string;
  sessionId: string | null;
  skill: string;
  model?: string | null;
  runId: string; // For performance tracking
}

interface AdminAgentChatStreamData {
  isActive: boolean;
  isDone: boolean;
}

/**
 * Extended SSE event type for admin agent chat.
 * Includes CUSTOM event not in the base AG-UI types.
 */
type AdminAgentSSEEvent =
  | AGUISSEEvent
  | {
      type: 'CUSTOM';
      name: string;
      value: {
        session_id?: string;
        is_error?: boolean;
        num_turns?: number;
        duration_ms?: number;
        total_cost_usd?: number;
        title?: string;
        markdown?: string;
      };
      timestamp?: number;
    };

// ============================================================================
// Auth Token Management
// ============================================================================

const mutex = new Mutex();

// ============================================================================
// API Definition
// ============================================================================

export const agentStreamingApi = createApi({
  reducerPath: 'adminAgent/streaming',
  baseQuery: fakeBaseQuery(),
  endpoints: builder => ({
    streamAgentChat: builder.query<AdminAgentChatStreamData, AdminAgentChatRequest>({
      queryFn: () => ({
        data: {
          isActive: false,
          isDone: false,
        },
      }),

      async onCacheEntryAdded(
        payload,
        // eslint-disable-next-line @typescript-eslint/unbound-method
        { updateCachedData, cacheEntryRemoved, dispatch },
      ) {
        const controller = new AbortController();

        // Performance tracking
        const requestStartTime = Date.now();
        const runId = payload.runId;

        let connectionOpenTime: number | null = null;
        let firstEventTime: number | null = null;
        let firstTokenTime: number | null = null;
        let streamEndTime: number | null = null;
        let eventCount = 0;
        let outcome: 'success' | 'interrupt' | 'error' = 'success';
        let metricsDispatched = false;

        const thresholdMs = parseInt(
          String(import.meta.env.VITE_SSE_PERFORMANCE_THRESHOLD_MS || '10000'),
          10,
        );

        const dispatchPerformanceMetrics = () => {
          if (metricsDispatched) return;
          if (!connectionOpenTime || !firstEventTime || !streamEndTime) return;

          metricsDispatched = true;

          const metrics: SSEPerformanceMetrics = {
            time_to_connection_ms: connectionOpenTime - requestStartTime,
            time_to_first_event_ms: firstEventTime - requestStartTime,
            time_to_first_token_ms: firstTokenTime
              ? firstTokenTime - requestStartTime
              : firstEventTime - requestStartTime,
            total_duration_ms: streamEndTime - requestStartTime,
            run_id: runId,
            thread_id: payload.sessionId || undefined,
            outcome,
            event_count: eventCount,
            exceeded_threshold: streamEndTime - requestStartTime > thresholdMs,
            threshold_ms: thresholdMs,
          };

          dispatch(setSSEPerformanceMetrics(metrics));
        };

        await mutex.waitForUnlock();

        let token: string;
        try {
          token = await getAuth0AccessTokenSilently();
        } catch (error) {
          console.error('Failed to get access token for streaming:', error);
          dispatch(setError('Authentication failed. Please log in again.'));
          outcome = 'error';
          streamEndTime = Date.now();
          dispatchPerformanceMetrics();
          return;
        }

        (async () => {
          try {
            await fetchEventSource(`${API_BASE_URL}/v2/admin/agent/chat`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                prompt: payload.prompt,
                session_id: payload.sessionId,
                skill: payload.skill,
                model: payload.model ?? null,
              }),
              signal: controller.signal,

              async onopen(res) {
                if (!res.ok) {
                  const errorText = await res.text();
                  if (res.status === 401) {
                    if (!mutex.isLocked()) {
                      const release = await mutex.acquire();
                      try {
                        dispatch(
                          setError('Session expired. Please refresh the page and log in again.'),
                        );
                      } finally {
                        release();
                      }
                    } else {
                      await mutex.waitForUnlock();
                      dispatch(setError('Session expired. Please try again or refresh the page.'));
                    }
                  } else {
                    dispatch(setError(`Failed to connect: ${res.status} ${res.statusText}`));
                  }

                  updateCachedData(draft => {
                    draft.isActive = false;
                  });

                  throw new Error(`Stream failed with status ${res.status}: ${errorText}`);
                }

                console.debug('📡 [SSE] Admin agent chat connection opened');

                if (!connectionOpenTime) {
                  connectionOpenTime = Date.now();
                }

                updateCachedData(draft => {
                  draft.isActive = true;
                  draft.isDone = false;
                });
              },

              onmessage: event => {
                if (!event.data) return;

                if (!firstEventTime) {
                  firstEventTime = Date.now();
                }
                eventCount++;

                let data: AdminAgentSSEEvent;
                try {
                  data = JSON.parse(event.data) as AdminAgentSSEEvent;
                } catch {
                  console.warn('Failed to parse SSE event:', event.data);
                  return;
                }

                console.debug('📡 [SSE] Admin agent event:', {
                  type: data.type,
                });

                switch (data.type) {
                  case 'RUN_STARTED': {
                    const newThreadId = 'threadId' in data ? data.threadId : null;
                    if (newThreadId) {
                      dispatch(setThreadId(newThreadId));
                    }
                    dispatch(setAssistantStatus('thinking'));
                    dispatch(clearToolCalls());
                    break;
                  }

                  case 'STEP_STARTED': {
                    const stepName = 'step_name' in data ? data.step_name : '';
                    if (stepName) {
                      dispatch(setCurrentStep(stepName));
                    }
                    break;
                  }

                  case 'ACTIVITY_SNAPSHOT': {
                    const activityData = data as {
                      type: 'ACTIVITY_SNAPSHOT';
                      messageId: string;
                      activityType: string;
                      content: { message: string };
                      timestamp: number;
                      replace?: boolean;
                    };
                    dispatch(
                      setActivitySnapshot({
                        messageId: activityData.messageId,
                        activityType: activityData.activityType as 'THINKING' | 'PLAN' | 'SEARCH',
                        message: activityData.content.message,
                        timestamp: activityData.timestamp,
                        replace: activityData.replace,
                      }),
                    );
                    break;
                  }

                  case 'TEXT_MESSAGE_START': {
                    const messageId = 'messageId' in data ? data.messageId : crypto.randomUUID();
                    // Only start streaming for assistant messages (no namespace = chat message)
                    if (!('namespace' in data) || !data.namespace) {
                      dispatch(startStreamingMessage(messageId || crypto.randomUUID()));
                    }
                    break;
                  }

                  case 'TEXT_MESSAGE_CONTENT': {
                    const delta = 'delta' in data ? data.delta : '';
                    if (delta && (!('namespace' in data) || !data.namespace)) {
                      dispatch(appendStreamingContent(delta));
                      if (!firstTokenTime) {
                        firstTokenTime = Date.now();
                      }
                    }
                    break;
                  }

                  case 'TEXT_MESSAGE_END': {
                    if (!('namespace' in data) || !data.namespace) {
                      dispatch(finalizeMessage());
                    }
                    break;
                  }

                  case 'TOOL_CALL_START': {
                    const toolCallId =
                      'toolCallId' in data ? data.toolCallId : crypto.randomUUID();
                    const toolCallName =
                      'toolCallName' in data ? data.toolCallName : 'unknown';
                    dispatch(addToolCall({ toolCallId, toolCallName }));
                    break;
                  }

                  case 'TOOL_CALL_ARGS': {
                    const toolCallId = 'toolCallId' in data ? data.toolCallId : '';
                    const delta = 'delta' in data ? data.delta : '';
                    if (toolCallId && delta) {
                      dispatch(appendToolCallArgs({ toolCallId, delta }));
                    }
                    break;
                  }

                  case 'TOOL_CALL_END': {
                    const toolCallId = 'toolCallId' in data ? data.toolCallId : '';
                    if (toolCallId) {
                      dispatch(removeToolCall(toolCallId));
                    }
                    break;
                  }

                  case 'CUSTOM': {
                    const customData = data as {
                      type: 'CUSTOM';
                      name: string;
                      value: {
                        session_id?: string;
                        is_error?: boolean;
                        title?: string;
                        markdown?: string;
                      };
                    };
                    if (customData.name === 'agent_result' && customData.value?.session_id) {
                      dispatch(setSessionId(customData.value.session_id));
                    }
                    if (
                      customData.name === 'markdown_payload' &&
                      customData.value?.title &&
                      customData.value?.markdown
                    ) {
                      dispatch(
                        addMarkdownPayload({
                          title: customData.value.title,
                          markdown: customData.value.markdown,
                        }),
                      );
                    }
                    break;
                  }

                  case 'RUN_FINISHED': {
                    const runFinishedData = data as {
                      type: 'RUN_FINISHED';
                      outcome?: 'success' | 'interrupt' | 'error';
                      message?: string;
                    };

                    if (runFinishedData.outcome === 'error') {
                      dispatch(
                        setError(runFinishedData.message || 'Agent encountered an error.'),
                      );
                      outcome = 'error';
                    } else {
                      dispatch(completeWorkflow());
                      outcome = 'success';
                    }

                    updateCachedData(draft => {
                      draft.isActive = false;
                      draft.isDone = true;
                    });
                    streamEndTime = Date.now();
                    dispatchPerformanceMetrics();
                    break;
                  }

                  case 'error': {
                    const errorData = data as { type: 'error'; message?: string };
                    dispatch(setError(errorData.message || 'An error occurred.'));
                    outcome = 'error';
                    streamEndTime = Date.now();
                    dispatchPerformanceMetrics();
                    break;
                  }

                  default:
                    console.debug(
                      '📡 [SSE] Unhandled event type:',
                      (data as { type: string }).type,
                    );
                }
              },

              onerror: err => {
                console.error('📡 [SSE] Stream error:', err);
                dispatch(setError('Connection error. Please try again.'));
                outcome = 'error';
                streamEndTime = Date.now();
                dispatchPerformanceMetrics();

                updateCachedData(draft => {
                  draft.isActive = false;
                  draft.isDone = true;
                });

                throw err;
              },
            });
          } catch (error) {
            console.error('📡 [SSE] Stream failed:', error);
            dispatch(setError('Failed to connect to agent service. Please try again.'));
            outcome = 'error';
            streamEndTime = Date.now();
            dispatchPerformanceMetrics();

            updateCachedData(draft => {
              draft.isActive = false;
              draft.isDone = true;
            });
          }
        })();

        await cacheEntryRemoved;
        controller.abort();
      },
    }),
  }),
});

export const { useStreamAgentChatQuery } = agentStreamingApi;
